import { IPQuestion } from "modules/organization/models/interface";
import { EquationParser, ILogicalExpression, RQL_OPERATORS } from "./equationParser";

const OPERATOR_PATTERN = new RegExp(`^(${RQL_OPERATORS.join("|")})$`);

const COMPARE_OPERATORS = ["=", "!=", "<", ">", ">=", "<="];

const COMPARE_TYPES = ["NUMBER", "DATE"];

// Open-ended question types (Numeric, Text, Currency, Date — see renderQuestionType in
// modules/organization/utility/commonFunctions.tsx for the canonical id-to-label mapping) get a
// synthesized "{variable}_answer_exists" / "{variable}_answer_not_exists" response pair instead
// of real categorical response options (see fn_get_ranking_project_question_snapshot.sql).
// "_answer_exists"'s value is the actual typed answer (see
// fn_get_ranking_project_record_answers.sql) — it's exactly how EQUAL/NOTEQUAL/COMPARE are meant
// to reach an open-ended answer's content, and AND/OR can meaningfully use it too ("was this
// answered"). "_answer_not_exists" is different: its value is null EVERY time it appears, by
// construction — it can never evaluate truthy in any operator, so it's restricted below.
// QHASANSWER/QHASNOTANSWER on the QUESTION variable is the correct way to check "was this
// answered" without relying on either half of this pair directly.
export const OPEN_ENDED_QUESTION_TYPE_IDS = [1, 2, 3, 7];
const ANSWER_NOT_EXISTS_SUFFIX = "_answer_not_exists";

// Maps each open-ended question's "_answer_not_exists" response variable back to its owning
// question variable, so the error message can point the user at the QHASNOTANSWER replacement.
// Deliberately does NOT include "_answer_exists" — that one is fully valid to use. Exported so
// equationErrorRanges.ts's independent walk can flag the same variable for the squiggle
// underline without duplicating this lookup.
export const buildAnswerNotExistsResponseVariableMap = (
  questions: IPQuestion[],
): Map<string, string> => {
  const result = new Map<string, string>();
  questions.forEach((question) => {
    if (OPEN_ENDED_QUESTION_TYPE_IDS.includes(question.question_type_id)) {
      question.responses.forEach((response) => {
        if (response.response_variable.endsWith(ANSWER_NOT_EXISTS_SUFFIX)) {
          result.set(response.response_variable, question.question_variable);
        }
      });
    }
  });
  return result;
};

// QAND/QOR's engine (calculateEquation.ts's operatorQAND/operatorQOR) runs AND/OR over EVERY
// one of the question's own response variables — including its synthesized "_answer_not_exists"
// half for an open-ended question, which is restricted above and always throws. So QAND/QOR on
// any open-ended question (types 1/2/3/7) is unusable by construction, not just discouraged;
// QHASANSWER/QHASNOTANSWER (which check "was this answered" directly, not per-response-option)
// are the correct replacement and remain unaffected — this set is only consulted for QAND/QOR.
export const buildOpenEndedQuestionVariableSet = (questions: IPQuestion[]): Set<string> => {
  const result = new Set<string>();
  questions.forEach((question) => {
    if (OPEN_ENDED_QUESTION_TYPE_IDS.includes(question.question_type_id)) {
      result.add(question.question_variable);
    }
  });
  return result;
};

// Walks an already-parsed (syntactically valid) equation tree and collects EVERY content error
// found — unknown variable, wrong argument count for an operator, unknown IMPORT target,
// IMPORT tier-order violation — rather than stopping at the first one, so all of them can be
// surfaced together. `exprPair` is always `[operatorName, argsArray]`; `argsArray` is a mix of
// plain string arguments belonging to this operator and nested `[operatorName, argsArray]` pairs
// (sub-expressions), which are recursed into independently.
const collectEquationContentErrors = (
  exprPair: ILogicalExpression,
  knownQuestionVars: Set<string>,
  knownResponseVars: Set<string>,
  answerNotExistsResponseVars: Map<string, string>,
  openEndedQuestionVars: Set<string>,
  rankDisplayOrderByName: Map<string, number | null | undefined>,
  selfDisplayOrder: number | null | undefined,
  errors: Set<string>,
) => {
  const operatorName =
    typeof exprPair[0] === "string" ? exprPair[0].toUpperCase() : "";
  const argsArray = exprPair[1];
  if (!Array.isArray(argsArray)) {
    return;
  }

  // Only AND/OR's calculation engine (calculateEquation.ts's operatorAND/operatorOR) actually
  // accepts a nested operator call as one of its own arguments (e.g. AND(a, OR(b,c))) — every
  // other operator (IMPORT, EQUAL, COMPARE, QAND, QHASANSWER, etc.) only ever takes plain string
  // arguments. So the "this looks like a nested sub-expression, validate it separately and don't
  // count it" treatment below must only apply for AND/OR — otherwise e.g.
  // IMPORT(Rank2, AND(x)) silently swallows "AND", [...] as an unrelated nested pair, leaving
  // plainArgs at length 1 ("Rank2") and hiding the real problem: IMPORT was actually given two
  // arguments, and it doesn't support a nested expression as either of them.
  const allowsNestedExpressions = operatorName === "AND" || operatorName === "OR";

  // Only plain string arguments count toward an operator's own parameter list/role checks — a
  // nested operator call (e.g. AND's argument being another OR(...)) is validated independently
  // via the recursive call below, not counted as one of THIS operator's plain parameters.
  const plainArgs: string[] = [];
  for (let i = 0; i < argsArray.length; i++) {
    const item = argsArray[i];
    const next = argsArray[i + 1];
    if (
      allowsNestedExpressions &&
      typeof item === "string" &&
      OPERATOR_PATTERN.test(item.toUpperCase()) &&
      Array.isArray(next)
    ) {
      collectEquationContentErrors(
        [item, next],
        knownQuestionVars,
        knownResponseVars,
        answerNotExistsResponseVars,
        openEndedQuestionVars,
        rankDisplayOrderByName,
        selfDisplayOrder,
        errors,
      );
      i++; // the nested pair consumed `next` too — don't re-visit it as a plain arg
      continue;
    }
    if (typeof item === "string") {
      plainArgs.push(item);
    }
  }

  const answerNotExistsError = (variable: string): string => {
    const questionVar = answerNotExistsResponseVars.get(variable);
    return `${variable} can't be used here — use QHASNOTANSWER(${questionVar}) instead.`;
  };

  if (operatorName === "AND" || operatorName === "OR") {
    // The real calculation engine (calculateEquation.ts) places no minimum/maximum on AND/OR's
    // argument count — 0 args silently computes to 0 rather than throwing — so no count check is
    // added here either, to avoid blocking Save on something that would actually run fine.
    plainArgs.forEach((variable) => {
      if (!knownResponseVars.has(variable)) {
        errors.add(`Response variable not found: ${variable}`);
      } else if (answerNotExistsResponseVars.has(variable)) {
        errors.add(answerNotExistsError(variable));
      }
    });
  } else if (operatorName === "QAND" || operatorName === "QOR") {
    if (plainArgs.length !== 1) {
      errors.add(
        `${operatorName} takes exactly one question variable (found ${plainArgs.length}).`,
      );
    } else if (!knownQuestionVars.has(plainArgs[0])) {
      errors.add(`Question variable not found: ${plainArgs[0]}`);
    } else if (openEndedQuestionVars.has(plainArgs[0])) {
      errors.add(
        `${operatorName}(${plainArgs[0]}) can't be used here — this question doesn't support ${operatorName}. Use QHASANSWER or QHASNOTANSWER instead.`,
      );
    }
  } else if (operatorName === "QHASANSWER" || operatorName === "QHASNOTANSWER") {
    if (plainArgs.length !== 1) {
      errors.add(
        `${operatorName} takes exactly one question variable (found ${plainArgs.length}).`,
      );
    } else if (!knownQuestionVars.has(plainArgs[0])) {
      errors.add(`Question variable not found: ${plainArgs[0]}`);
    }
  } else if (operatorName === "EQUAL" || operatorName === "NOTEQUAL") {
    if (plainArgs.length !== 2) {
      errors.add(
        `${operatorName} takes exactly two parameters — a response variable and a comparison value (found ${plainArgs.length}).`,
      );
    } else if (!knownResponseVars.has(plainArgs[0])) {
      errors.add(`Response variable not found: ${plainArgs[0]}`);
    } else if (answerNotExistsResponseVars.has(plainArgs[0])) {
      errors.add(answerNotExistsError(plainArgs[0]));
    }
  } else if (operatorName === "COMPARE") {
    if (plainArgs.length !== 3 && plainArgs.length !== 4) {
      errors.add(
        `COMPARE takes three parameters — a response variable, a comparison operator, and a value — plus an optional fourth parameter (NUMBER or DATE) (found ${plainArgs.length}).`,
      );
    } else {
      if (!knownResponseVars.has(plainArgs[0])) {
        errors.add(`Response variable not found: ${plainArgs[0]}`);
      } else if (answerNotExistsResponseVars.has(plainArgs[0])) {
        errors.add(answerNotExistsError(plainArgs[0]));
      }
      if (!COMPARE_OPERATORS.includes(plainArgs[1])) {
        errors.add(
          `COMPARE's second parameter must be one of ${COMPARE_OPERATORS.join(", ")} — found '${plainArgs[1]}'.`,
        );
      }
      if (
        plainArgs.length === 4 &&
        !COMPARE_TYPES.includes(plainArgs[3].toUpperCase())
      ) {
        errors.add(
          `COMPARE's fourth parameter must be one of ${COMPARE_TYPES.join(", ")} — found '${plainArgs[3]}'.`,
        );
      }
    }
  } else if (operatorName === "IMPORT") {
    if (plainArgs.length !== 1) {
      errors.add(
        `IMPORT takes exactly one rank/equation name (found ${plainArgs.length}).`,
      );
    } else {
      const rankName = plainArgs[0];
      if (!rankDisplayOrderByName.has(rankName)) {
        errors.add(`Rank not found: ${rankName}`);
      } else if (selfDisplayOrder != null) {
        const targetDisplayOrder = rankDisplayOrderByName.get(rankName);
        if (targetDisplayOrder != null) {
          if (targetDisplayOrder === selfDisplayOrder) {
            errors.add(
              `${rankName} is this same rank — a rank cannot IMPORT itself.`,
            );
          } else if (targetDisplayOrder < selfDisplayOrder) {
            errors.add(
              `${rankName} is a higher rank and cannot be imported into a lower rank.`,
            );
          }
        }
      }
    }
  }
};

// Single source of truth for "is this equation OK to save" — syntax first (a specific, pointed
// reason, not a generic label), then every content problem found (unknown variable, wrong
// argument count per operator, unknown/higher-rank IMPORT target). Used both for the Equation
// Editor's live, per-keystroke error list and as a server-call guard in the store's
// saveRankingEquation, so the same rules apply whichever way a save is triggered.
export const validateEquationContent = (
  equationText: string,
  questions: IPQuestion[],
  rankDisplayOrderByName: Map<string, number | null | undefined>,
  selfDisplayOrder: number | null | undefined,
): string[] => {
  if (!equationText.trim()) {
    return [];
  }

  const equationParsedObj = new EquationParser(equationText);
  if (equationParsedObj.getHasError()) {
    return [equationParsedObj.getSyntaxErrorMessage()];
  }

  const knownQuestionVars = new Set(
    questions.map((each) => each.question_variable),
  );
  const knownResponseVars = new Set(
    questions.flatMap((each) =>
      each.responses.map((eachRes) => eachRes.response_variable),
    ),
  );
  const answerNotExistsResponseVars = buildAnswerNotExistsResponseVariableMap(questions);
  const openEndedQuestionVars = buildOpenEndedQuestionVariableSet(questions);

  const errors = new Set<string>();
  const exprPair = equationParsedObj.getEquationStack();
  if (Array.isArray(exprPair) && exprPair.length === 2) {
    collectEquationContentErrors(
      exprPair,
      knownQuestionVars,
      knownResponseVars,
      answerNotExistsResponseVars,
      openEndedQuestionVars,
      rankDisplayOrderByName,
      selfDisplayOrder,
      errors,
    );
  }
  return Array.from(errors);
};
